import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, roleMeta, ConfirmDialog, IconBtn } from "./shared";
import { Search, ScrollText, Lock, Unlock, Crown, ShieldCheck, UserCog, UserCheck, Power, KeyRound, Trash2 } from "lucide-react";

export function UsersPanel({ currentUser }) {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [confirm, setConfirm] = useState(null); // {title,message,confirmLabel,danger,action}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (q = "") => {
    try {
      const { data } = await api.get("/admin/users", { params: q ? { search: q } : {} });
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  const loadLog = useCallback(async () => {
    try { const { data } = await api.get("/admin/audit-log", { params: { limit: 50 } }); setLog(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); loadLog(); }, [load, loadLog]);
  useEffect(() => { const t = setTimeout(() => load(search), 350); return () => clearTimeout(t); }, [search, load]);

  const run = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); if (successMsg) toast.success(successMsg); await load(search); await loadLog(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); setConfirm(null); }
  };

  const setRole = (u, role) => setConfirm({
    title: role === "admin" ? `Promote ${u.name} to Admin?` : `Change ${u.name} to Customer?`,
    message: role === "admin" ? "Admins can manage everything in this dashboard." : "This user will lose admin access.",
    confirmLabel: role === "admin" ? "Promote to Admin" : "Make Customer",
    action: () => run(() => api.patch(`/admin/users/${u.user_id}/role`, { role }), "Role updated"),
  });
  const setStatus = (u, status) => run(() => api.patch(`/admin/users/${u.user_id}/status`, { status }),
    status === "inactive" ? "Account deactivated" : "Account activated");
  const setLock = (u, locked) => run(() => api.patch(`/admin/users/${u.user_id}/lock`, { locked }),
    locked ? "Account locked" : "Account unlocked");
  const sendReset = (u) => run(() => api.post(`/admin/users/${u.user_id}/send-reset`), "Password reset email sent");
  const del = (u) => setConfirm({
    title: `Delete ${u.name}?`, danger: true,
    message: "This permanently removes the user. Prefer 'Deactivate' if you may need them later. This cannot be undone.",
    confirmLabel: "Delete permanently",
    action: () => run(() => api.delete(`/admin/users/${u.user_id}`), "User deleted"),
  });

  if (!users) return <Loader />;
  const admins = users.filter((u) => u.role === "admin").length;

  return (
    <div data-testid="users-panel">
      <Head title="Team & Users" desc="Manage roles, access and account status. Every action is logged." />
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="users-search"
            placeholder="Search by name or email…" className="w-full bg-navy-800 border border-white/10 text-white pl-10 pr-4 py-2.5 focus:border-gold focus:outline-none text-sm" />
        </div>
        <div className="flex gap-4 text-sm text-gray-400">
          <span><span className="text-white font-semibold">{users.length}</span> users</span>
          <span><span className="text-white font-semibold">{admins}</span> admins</span>
        </div>
        <button onClick={() => setShowLog((s) => !s)} data-testid="toggle-audit-log"
          className="flex items-center gap-2 text-sm border border-white/15 text-gray-300 px-3 py-2 hover:bg-white/5 transition-colors">
          <ScrollText size={15} /> {showLog ? "Hide" : "View"} activity log
        </button>
      </div>

      {showLog && (
        <div className="bg-navy-800 border border-white/10 p-4 mb-6 max-h-72 overflow-y-auto" data-testid="audit-log">
          {log.length === 0 ? <p className="text-gray-500 text-sm">No admin actions logged yet.</p> : log.map((e) => (
            <div key={e.id} className="text-sm py-2 border-b border-white/5 flex flex-wrap gap-x-2">
              <span className="text-gold">{e.actor_name || e.actor_email}</span>
              <span className="text-gray-300">{e.action.replace(/_/g, " ")}</span>
              {e.target_email && <span className="text-gray-400">→ {e.target_email}</span>}
              {e.details && <span className="text-gray-500">· {e.details}</span>}
              <span className="text-gray-600 ml-auto">{new Date(e.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-navy-800 border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-white/10">
              <th className="px-4 py-3 font-medium">User</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Joined</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const rm = roleMeta(u);
              const inactive = u.status === "inactive";
              return (
                <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{u.name}</div>
                    <div className="text-gray-500 text-xs">{u.email} · {u.auth_provider}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 border ${rm.cls}`} data-testid={`user-role-${u.user_id}`}>
                      <rm.Icon size={12} /> {rm.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center gap-1 text-xs ${inactive ? "text-red-300" : "text-green-300"}`} data-testid={`user-status-${u.user_id}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${inactive ? "bg-red-400" : "bg-green-400"}`} /> {inactive ? "Inactive" : "Active"}
                      </span>
                      {u.is_locked && <span className="inline-flex items-center gap-1 text-xs text-amber-300"><Lock size={11} /> Locked</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    {u.is_owner ? (
                      <span className="text-xs text-gray-500 flex items-center justify-end gap-1"><Crown size={12} className="text-gold" /> Protected</span>
                    ) : (
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {u.role === "admin"
                          ? <IconBtn title="Demote to Customer" testid={`demote-${u.user_id}`} onClick={() => setRole(u, "client")}><UserCog size={15} /></IconBtn>
                          : <IconBtn title="Promote to Admin" testid={`promote-${u.user_id}`} onClick={() => setRole(u, "admin")}><ShieldCheck size={15} /></IconBtn>}
                        {inactive
                          ? <IconBtn title="Activate" testid={`activate-${u.user_id}`} onClick={() => setStatus(u, "active")}><UserCheck size={15} /></IconBtn>
                          : <IconBtn title="Deactivate" testid={`deactivate-${u.user_id}`} onClick={() => setStatus(u, "inactive")}><Power size={15} /></IconBtn>}
                        {u.is_locked
                          ? <IconBtn title="Unlock" testid={`unlock-${u.user_id}`} onClick={() => setLock(u, false)}><Unlock size={15} /></IconBtn>
                          : <IconBtn title="Lock" testid={`lock-${u.user_id}`} onClick={() => setLock(u, true)}><Lock size={15} /></IconBtn>}
                        <IconBtn title="Send password reset" testid={`reset-${u.user_id}`} onClick={() => sendReset(u)}><KeyRound size={15} /></IconBtn>
                        <IconBtn title="Delete permanently" testid={`delete-${u.user_id}`} danger onClick={() => del(u)}><Trash2 size={15} /></IconBtn>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog open={!!confirm} busy={busy} {...(confirm || {})}
        onConfirm={() => confirm?.action?.()} onCancel={() => setConfirm(null)} />
    </div>
  );
}
